import { createPointHubSDK } from '@ripple/point-hub-web-sdk';
import { getPointHubConfigValidationErrors } from './config';
import { emitPointHubDiagnostic } from './diagnostics';
import { getPointHubRuntimeCache } from './runtime-cache';
import type { PointHubClientFactoryResult, PointHubConfig, PointHubRuntimeSupportDetail } from './types';

interface PointHubRuntimeLike {
  fetch?: unknown;
  TextEncoder?: unknown;
  crypto?: {
    getRandomValues?: unknown;
    subtle?: unknown;
  };
}

const getDefaultRuntime = (): PointHubRuntimeLike => {
  return typeof globalThis === 'undefined' ? {} : globalThis;
};

export const getPointHubRuntimeSupportDetail = (
  runtime: PointHubRuntimeLike = getDefaultRuntime()
): PointHubRuntimeSupportDetail => ({
  fetch: typeof runtime.fetch === 'function',
  textEncoder: typeof runtime.TextEncoder === 'function',
  crypto: Boolean(runtime.crypto),
  cryptoGetRandomValues: Boolean(runtime.crypto && typeof runtime.crypto.getRandomValues === 'function'),
  cryptoSubtle: Boolean(runtime.crypto && runtime.crypto.subtle)
});

export const isPointHubRuntimeSupported = (
  detail: PointHubRuntimeSupportDetail = getPointHubRuntimeSupportDetail()
): boolean => {
  return detail.fetch && detail.textEncoder && detail.crypto && detail.cryptoGetRandomValues && detail.cryptoSubtle;
};

const getCredentialMarker = (credential?: string): string => {
  if (!credential) return 'none';
  let hash = 2166136261;
  for (let index = 0; index < credential.length; index += 1) {
    hash ^= credential.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${credential.length}:${(hash >>> 0).toString(16)}`;
};

const getClientKey = (config: PointHubConfig): string => {
  return [
    config.endpoint || '',
    config.appId || '',
    config.environment || '',
    config.sourceName,
    config.sourceType,
    getCredentialMarker(config.appSecret)
  ].join('|');
};

export const createScenarioClient = (config: PointHubConfig): PointHubClientFactoryResult => {
  if (!config.enabled) return { client: null, unavailableReason: 'point_hub_disabled' };
  if (getPointHubConfigValidationErrors(config).length > 0) {
    return { client: null, unavailableReason: 'point_hub_config_invalid' };
  }
  const runtimeDetail = getPointHubRuntimeSupportDetail();
  if (!isPointHubRuntimeSupported(runtimeDetail)) {
    return {
      client: null,
      unavailableReason: 'point_hub_runtime_unsupported',
      unavailableDetail: runtimeDetail
    };
  }

  return {
    client: createPointHubSDK({
      endpoint: config.endpoint as string,
      appId: config.appId as string,
      appSecret: config.appSecret as string,
      environment: config.environment,
      sourceName: config.sourceName,
      sourceType: config.sourceType,
      debug: config.debug,
      onError: (error) => {
        emitPointHubDiagnostic({
          code: 'sdk_error',
          environment: config.environment,
          detail: {
            sdkCode: error.code,
            status: error.status,
            requestId: error.requestId
          }
        });
      }
    })
  };
};

export const getOrCreateScenarioClient = (config: PointHubConfig): PointHubClientFactoryResult => {
  const cache = getPointHubRuntimeCache();
  const clientKey = getClientKey(config);
  if (cache.clientKey === clientKey && cache.client) return { client: cache.client };

  const result = createScenarioClient(config);
  cache.clientKey = result.client ? clientKey : null;
  cache.client = result.client;
  cache.initPromise = null;
  return result;
};
